import { useState } from 'react'
import type { CortexSession } from '../services/api'

function formatRelativeDate(iso: string): string {
  try {
    const d = new Date(iso)
    const now = Date.now()
    const diff = (now - d.getTime()) / 1000
    if (diff < 60) return 'gerade eben'
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`
    if (diff < 604800) return `vor ${Math.floor(diff / 86400)} Tagen`
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return ''
  }
}

interface AriaSessionsSidebarProps {
  sessions: CortexSession[]
  currentSessionId?: string
  sessionsLoading: boolean
  sessionsError: string | null
  collapsed: boolean
  disabled?: boolean
  onSelect: (sessionId: string) => void
  onNew: () => void
  onRename: (sessionId: string) => void
  onDelete: (sessionId: string) => void
}

export default function AriaSessionsSidebar({
  sessions,
  currentSessionId,
  sessionsLoading,
  sessionsError,
  collapsed,
  disabled,
  onSelect,
  onNew,
  onRename,
  onDelete,
}: AriaSessionsSidebarProps) {
  const [menuForSession, setMenuForSession] = useState<string | null>(null)

  return (
    <aside className={`aria-sessions-sidebar ${collapsed ? 'aria-sessions-sidebar--collapsed' : ''}`}>
      <div className="aria-sessions-header">
        <button
          className="aria-sessions-new-btn"
          onClick={onNew}
          title="Neue Unterhaltung"
          disabled={disabled}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span>Neue Session</span>
        </button>
      </div>

      <div className="aria-sessions-list">
        {sessionsLoading && sessions.length === 0 && (
          <div className="aria-sessions-empty">Lädt…</div>
        )}
        {!sessionsLoading && sessions.length === 0 && (
          <div className="aria-sessions-empty">Noch keine Sessions.</div>
        )}
        {sessions.map(s => (
          <div
            key={s.id}
            className={`aria-session-item ${s.id === currentSessionId ? 'aria-session-item--active' : ''}`}
          >
            <button
              className="aria-session-item-main"
              onClick={() => { setMenuForSession(null); onSelect(s.id) }}
              title={s.title}
            >
              <span className="aria-session-title">{s.title || 'Neuer Chat'}</span>
              <span className="aria-session-date">{formatRelativeDate(s.last_message_at)}</span>
            </button>
            <button
              className="aria-session-menu-btn"
              onClick={(e) => {
                e.stopPropagation()
                setMenuForSession(menuForSession === s.id ? null : s.id)
              }}
              title="Aktionen"
              aria-label="Aktionen"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                <circle cx="5" cy="12" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="19" cy="12" r="1.5" />
              </svg>
            </button>
            {menuForSession === s.id && (
              <div className="aria-session-menu" onMouseLeave={() => setMenuForSession(null)}>
                <button onClick={() => { setMenuForSession(null); onRename(s.id) }}>Umbenennen</button>
                <button onClick={() => { setMenuForSession(null); onDelete(s.id) }} className="aria-session-menu-danger">Löschen</button>
              </div>
            )}
          </div>
        ))}
        {sessionsError && (
          <div className="aria-sessions-error">{sessionsError}</div>
        )}
      </div>
    </aside>
  )
}
